/**
 * Верхня панель (UX-01; layout.md, розд. 1): знак, позначка середовища, поле пошуку (відкриває
 * палітру `Ctrl+K`, UX-10), місце екрана (у картці — шлях і «8 з 86 ‹ ›», `topbar-slot.tsx`), «+»
 * швидкого створення (після входу, DAT-01), дзвіночок, меню користувача.
 */
import { Link } from '@tanstack/react-router';
import { isMac, kbdLabel } from '../app/hotkeys.ts';
import { EnvBadge } from '../brand/EnvBadge.tsx';
import { Mark, Wordmark } from '../brand/Logo.tsx';
import { NotificationBell } from '../features/notifications/Bell.tsx';
import { keys as k } from '../i18n/uk/keys.ts';
import { shell as t } from '../i18n/uk/shell.ts';
import { countMouseOpen, openPalette } from '../palette/store.ts';
import { openQuickCreate } from '../platform/quick-create.ts';
import { getPrefs } from '../prefs/prefs.ts';
import { ModKbd } from '../ui/Chord.tsx';
import { Icon } from '../ui/Icon.tsx';
import { toast } from '../ui/notify/index.ts';
import { UserMenu } from './UserMenu.tsx';
import type { ShellUser } from './user.ts';

export function Topbar({
  user,
  signedIn = user?.signedIn ?? false,
  slotRef,
}: {
  user: ShellUser | null;
  /** Вхід у платформу (метадані `/meta` є): «+» швидкого створення діє. */
  signedIn?: boolean;
  /** Елемент місця екрана (layout.md, розд. 7, правило 6): оболонка віддає його контекстом. */
  slotRef?: (el: HTMLDivElement | null) => void;
}) {
  return (
    <header className="topbar">
      {/* Бренд-бук, розд. 7: < 1536 px — лише знак 24 px (рейка 64 px), ≥ 1536 і розгорнута
          навігація — повний логотип; плашка середовища — одразу після знака. */}
      <Link to="/" className="topbar__logo" aria-label={t.logoHome}>
        <Mark size={24} />
        <Wordmark markSize={24} className="logo-text" />
      </Link>
      <EnvBadge />
      <button
        className="topbar__search"
        type="button"
        aria-haspopup="dialog"
        aria-keyshortcuts={isMac() ? 'Meta+K' : 'Control+K'}
        onClick={(e) => {
          openPalette('', e.timeStamp);
          // Порада про клавіші — лише після третього відкриття саме мишею (UX-12).
          if (e.detail > 0 && countMouseOpen() && getPrefs().keyHints)
            toast({ text: k.paletteTip(kbdLabel('K')) });
        }}
      >
        <Icon name="search" />
        <span className="truncate">{t.search}</span>
        {/* Клавіші ОС користувача: «⌘K» на macOS, «Ctrl+K» деінде (keyboard.md, п. 5) */}
        <ModKbd k="K" />
      </button>
      <span id="soon-hint" className="sr-only">
        {t.searchSoon}
      </span>
      <div className="topbar__ctx" ref={slotRef} />
      <span className="spacer" />
      {signedIn ? (
        // Швидке створення (DAT-01): діалог з типом запису; та сама дія — `N` поза карткою.
        <button
          className="btn btn--ghost btn--icon"
          type="button"
          aria-label={t.quickCreate}
          aria-haspopup="dialog"
          aria-keyshortcuts="N"
          title={`${t.quickCreate} (N)`}
          onClick={() => openQuickCreate()}
          data-testid="quick-create-open"
        >
          <Icon name="plus" />
        </button>
      ) : (
        <button
          className="btn btn--ghost btn--icon"
          type="button"
          aria-label={t.quickCreate}
          aria-disabled="true"
          aria-describedby="soon-hint"
          title={t.searchSoon}
        >
          <Icon name="plus" />
        </button>
      )}
      {signedIn ? (
        // Центр сповіщень (В4-W1, NTF-13): число, панель, тости — лише після входу в платформу.
        <NotificationBell />
      ) : (
        <button
          className="btn btn--ghost btn--icon topbar__bell"
          type="button"
          aria-label={t.notifications}
          aria-disabled="true"
          aria-describedby="soon-hint"
          title={t.searchSoon}
        >
          <Icon name="bell" />
        </button>
      )}
      <UserMenu user={user} />
    </header>
  );
}
